import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sun,
  Moon,
  User,
  Settings,
  LogOut,
  Grid3X3,
  ChevronDown,
  HelpCircle,
  Menu,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { toast } from "sonner";

import {
  getNotifications,
  MarkAsALLReadNotifications,
  MarkAsReadNotificationbyId,
  deleteAllNotification,
} from "../services/employeeService";
import { useSubscription } from "@/contexts/SubscriptionContext";
import NotificationsMenu from "@/components/common/NotificationsMenu";
import {
  getAdminNotificationRoute,
  getLinkedAdminNotificationRoute,
  announceNotificationsChanged,
  onNotificationsChanged,
} from "@/lib/notifications";
import { isLinkedAdmin } from "@/utils/workspace";

// `onOpenNav` opens the navigation drawer on phones (see AdminLayout).
const TopBar = ({ onOpenNav }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { addNotificationListener } = useWebSocket();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const { hasFeature } = useSubscription();

  // An admin who is also an employee: one person, so greet and show them by
  // name, and their profile is their employee profile.
  const linkedAdmin = isLinkedAdmin(user);
  const resolveNotificationRoute = linkedAdmin
    ? getLinkedAdminNotificationRoute(user.employee_id)
    : getAdminNotificationRoute;

  const companyLogoUrl = user.company_logo;
  const showCustomBranding = hasFeature("custom_branding") && companyLogoUrl;

  useEffect(() => {
    fetchNotifications();

    const unsubscribe = addNotificationListener((newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      toast.info(newNotification.title, {
        description: newNotification.message,
      });
    });
    return () => unsubscribe();
  }, []);

  // The Notifications page (or another bell) changed read / cleared state.
  useEffect(() => onNotificationsChanged(() => fetchNotifications()), []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await getNotifications();

      const sortedNotifications = response.data.sort((a, b) => {
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      setNotifications(sortedNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await MarkAsALLReadNotifications();
      toast.success("All notifications marked as read");
      announceNotificationsChanged();
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      // Refresh sidebar notification counts
      if (window.refreshSidebarNotifications) {
        window.refreshSidebarNotifications();
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to mark notifications as read");
    }
  };

  const clearAllNotifications = async () => {
    try {
      await deleteAllNotification();
      setNotifications([]);
      toast.success("All notifications cleared");
      announceNotificationsChanged();
    } catch (error) {
      console.error("Error clearing all notifications:", error);
      toast.error("Failed to clear notifications");
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read first
    try {
      await MarkAsReadNotificationbyId(notification._id, true);
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notification._id ? { ...n, is_read: true } : n
        )
      );
      announceNotificationsChanged();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
    // Where to go: shared with the Notifications page (lib/notifications.js).
    const route = resolveNotificationRoute(notification);
    if (route?.path) {
      navigate(route.path, route.state ? { state: route.state } : undefined);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return (
    <header className="h-16 bg-card border-b border-border flex items-center justify-between px-3 sm:px-6">
      {/* Left section */}
      <div className="flex min-w-0 flex-1 items-center space-x-2 sm:space-x-4">
        {/* Phones: the sidebar is a drawer */}
        {onOpenNav && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenNav}
            className="h-9 w-9 shrink-0 md:hidden"
            aria-label="Open menu"
            data-testid="open-nav-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Logo */}
        {showCustomBranding && (
          <img
            src={companyLogoUrl}
            alt="Company Logo"
            className="h-8 w-8 max-w-[160px] shrink-0 object-contain"
          />
        )}

        {/* Welcome text */}
        <div className="min-w-0">
          <h2 className="truncate text-base sm:text-lg font-semibold text-foreground">
            Welcome, {user?.role === "admin" && !linkedAdmin ? "Admin" : user?.username}
          </h2>
          <p className="hidden truncate text-sm text-muted-foreground sm:block">
            {new Date().toLocaleDateString("en-IN", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Right section */}
      <div className="flex shrink-0 items-center space-x-1 sm:space-x-4">
        {/* Notifications */}
        <NotificationsMenu
          notifications={notifications}
          unreadCount={unreadCount}
          loading={loading}
          onNotificationClick={handleNotificationClick}
          onMarkAll={markAllAsRead}
          onClearAll={clearAllNotifications}
          onViewAll={() => navigate("/notifications")}
          triggerClassName="relative h-8 w-8"
        />

        {/* Help Center */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/help")}
          className="hidden h-8 w-8 text-foreground hover:bg-accent sm:inline-flex"
          data-testid="help-center-btn"
          title="Help Center"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="hidden h-8 w-8 text-foreground hover:bg-accent sm:inline-flex"
          data-testid="theme-toggle-btn"
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDarkMode ? (
            <Sun className="h-4 w-4 text-yellow-400" />
          ) : (
            <Moon className="h-4 w-4 text-gray-700" />
          )}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 px-2 flex items-center space-x-2"
              data-testid="user-menu-btn"
            >
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs bg-primary text-primary-foreground dark:text-white">
                  {user?.role === "admin" && !linkedAdmin
                    ? "AD"
                    : user?.username
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-200 hidden md:inline">
                {user?.role === "admin" && !linkedAdmin
                  ? "Admin"
                  : user?.username?.split(" ")[0] || user?.username}
              </span>
              <ChevronDown className="h-3 w-3 text-gray-700 dark:text-gray-300" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-64 p-0 rounded-xl shadow-lg border dark:border-muted-foreground/20  bg-popover overflow-hidden"
          >
            {/* Header */}
            <div className="px-4 py-4 bg-muted/30">
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold">
                  {user?.username?.slice(0, 2).toUpperCase()}
                </div>

                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold">
                    {user?.username || "Administrator"}
                  </span>

                  <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                    {user?.email || "admin@company.com"}
                  </span>

                  {/* Role Badge */}
                  <span className="mt-1 w-fit text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
                    {user?.role?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Main Actions */}
            <div className="p-2">
              <DropdownMenuItem
                onClick={() =>
                  navigate(
                    user?.role === "employee" || linkedAdmin
                      ? "/employee/profile"
                      : "/admin/profile",
                  )
                }
                className="rounded-md cursor-pointer flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="text-sm">Profile</span>
              </DropdownMenuItem>

              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                className="rounded-md cursor-pointer flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors"
              >
                <Settings className="h-4 w-4" />
                <span className="text-sm">Settings</span>
              </DropdownMenuItem>

              {/* On phones Help and the theme switch live here, not in the bar. */}
              <DropdownMenuItem
                onClick={() => navigate("/help")}
                className="rounded-md cursor-pointer flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors sm:hidden"
              >
                <HelpCircle className="h-4 w-4" />
                <span className="text-sm">Help Center</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={toggleTheme}
                className="rounded-md cursor-pointer flex items-center gap-2 px-3 py-2 hover:bg-muted transition-colors sm:hidden"
              >
                {isDarkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                <span className="text-sm">{isDarkMode ? "Light mode" : "Dark mode"}</span>
              </DropdownMenuItem>
            </div>

            <div className="h-px bg-border" />

            {/* Logout */}
            <div className="p-2">
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="admin-logout"
                className="rounded-md cursor-pointer flex items-center gap-2 px-3 py-2 text-red-600 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-sm font-medium">Log out</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default TopBar;
